'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, t } from '@lingui/macro';
import { Button, MenuItem, Stack, ToggleButton } from '@mui/material';
import { SelectInput, ToggleButtonGroupInput } from '@prismo-io/design-system';
import {
  LanguageCode,
  type ToolsOrganizationsT,
  type ToolsProjectsT,
} from '@prismo-io/schemas';
import { type FC, useCallback } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useDownloadPDFDiagnostic } from './use-download-pdf-diagnostic';

type Props = {
  organization: Pick<ToolsOrganizationsT, 'id' | 'email'>;
  project: Pick<ToolsProjectsT, 'id' | 'name'>;
  onFinish: () => void;
};

const FormSchema = z.object({
  locale: LanguageCode,
  withComments: z.boolean({ coerce: true }),
});

type FormSchemaT = z.infer<typeof FormSchema>;

export const DownloadPdfForm: FC<Props> = (props) => {
  const { organization, project, onFinish } = props;

  const download = useDownloadPDFDiagnostic();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
  } = useForm<FormSchemaT>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    defaultValues: {
      locale: LanguageCode.Enum.fr,
      withComments: false,
    },
  });

  const watchedComment = watch('withComments');

  const onSubmit: SubmitHandler<FormSchemaT> = useCallback(
    (data) => {
      download({
        project,
        organization,
        ...data,
      });

      onFinish();
    },
    [onFinish, project, organization, download]
  );

  return (
    <Stack
      component="form"
      gap={2}
      onSubmit={handleSubmit(onSubmit)}
      padding={2}
      noValidate
    >
      {/* <Typography fontWeight="semibold" alignSelf="flex-start">
        <Trans>Configuration du rapport à générer</Trans>
      </Typography> */}

      <SelectInput
        control={control}
        disabled={isSubmitting}
        name="locale"
        label={t`Langue`}
        size="small"
        required
      >
        <MenuItem value={LanguageCode.Enum.fr}>
          <Trans>Français</Trans>
        </MenuItem>
        <MenuItem value={LanguageCode.Enum.en}>
          <Trans>Anglais</Trans>
        </MenuItem>
      </SelectInput>

      <ToggleButtonGroupInput
        control={control}
        name="withComments"
        size="small"
        exclusive
        disabled={isSubmitting}
        label={t`Inclure les commentaires`}
        unselectable="off"
        sx={{ flex: 1 }}
      >
        <ToggleButton
          value={true}
          size="small"
          color="primary"
          disabled={watchedComment === true}
        >
          <Trans>Oui</Trans>
        </ToggleButton>
        <ToggleButton
          value={false}
          size="small"
          color="primary"
          disabled={watchedComment === false}
        >
          <Trans>Non</Trans>
        </ToggleButton>
      </ToggleButtonGroupInput>

      <Stack alignItems="center" justifyContent="center">
        <Button disabled={isSubmitting || !isValid} type="submit" size="small">
          <Trans>Télécharger</Trans>
        </Button>
      </Stack>
    </Stack>
  );
};
